<template>
    <div class="flex flex-col">
        <div class="flex flex-col border-1 card-bg-dark md:flex-row invoice-section items-center ">
            <div class="w-full md:w-5/12 md:ml-3">
                <div class="card-bg  p-4 rounded-lg max-w-sm mx-auto">
                    <div class="flex flex-col items-center justify-between mb-2">
                        <span class=" font-medium">Current Balance</span>
                        <i class="pi pi-info-circle text-gray-400 cursor-help"
                            v-tooltip.bottom="'Your available balance for withdrawal'" />
                    </div>

                    <template v-if="userBalanceloading">
                        <Skeleton width="150px" height="2rem" class="mb-2" />
                    </template>
                    <div v-else class="text-2xl font-bold">
                        {{ formatCurrency(userBalance) }}
                    </div>
                    <div class="text-sm text-gray-500 mt-1 mb-4">
                        {{ withdrawalMessage }}
                    </div>  
                    <div class="flex flex-col gap-2">
                        <Button label="Withdraw" icon="pi pi-money-bill" severity="success" @click="isWithdrawalModalVisible = true"
                            :disabled="!canWithdraw" class="p-button-sm w-full" />
                        <Button label="View Transaction History" icon="pi pi-history" severity="secondary"
                            @click="toggleTransactions" class="p-button-sm p-button-outlined w-full" />
                    </div>
                </div>
            </div>
            <div v-if="invoices.length !== 0" class="invoice-container  w-7/12">
                <div v-for="(invoice, index) in invoices" :key="index" class="invoice"
                    @click="handleInvoiceClick(invoice)">
                    <div class="invoice-thumbnail">
                        <span class="invoice-month">{{ trimMonthName(invoice.monthName) }} {{ invoice.year }}</span>
                        <span class="invoice-download" @click.stop="downloadInvoice(invoice)">Download</span>
                    </div>
                </div>
            </div>
            <div v-else-if="invoices.length === 0">No invoices available</div>

        </div>

        <div class="invoice-viewer flex-grow bg-gray-1 flex justify-center mt-1">
            <div class="invoice-data">
                <div v-if="isLoading">
                    <Skeleton class="skelly w-full h-full skeleton-viewer" />
                </div>
                <div v-else>
                    <div v-if="pdfUrl !== null">
                        <PDF v-if="pdfUrl" showPageTooltip="true" :src="pdfUrl" class="pdf-viewer mb-6" />

                    </div>
                    <p v-else-if="invoices.length !== 0" class="text-center">Click on invoice to see details</p>
                </div>
            </div>
        </div>
    </div>
    <Transactions v-model:visible="isTransactionsVisible" :walletId="walletId" :currentBalance="userBalance" />
    <WithdrawalInvoiceModal v-model:visible="isWithdrawalModalVisible" :currentBalance="userBalance"
        @submitted="fetchWalletData" />
</template>

<script setup>
import { ref, onMounted, computed, nextTick } from 'vue';
import InvoiceService from '@/services/InvoiceService';
import { useStore } from 'vuex';
import { formatCurrency } from '@/utils/currencies';
import WalletService from '@/services/WalletService';
import Transactions from '@/components/finance/Transactions.vue';
import WithdrawalInvoiceModal from '@/components/finance/WithdrawalInvoiceModal.vue';
import { useWalletWithdrawal } from '@/utils/wallet-helper';
import { trimMonthName } from '@/utils/dateHelper';
import PDF from "pdf-vue3";
const store = useStore();
const user = computed(() => store.state.user);

const isLoading = ref(false);
const months = ref([]);

const invoices = ref([]);
const pdfUrl = ref(null);
const pdfCache = ref({});
const shouldShowPdf = ref(true);

const getDistinctMonths = (invoices) => {
    const monthsSet = new Set();
    invoices.forEach((invoice) => {
        monthsSet.add(invoice.monthName);
    });
    return Array.from(monthsSet)
        .sort((a, b) => (a - b)); // Sort by year and then month in ascending order
};

const getPdfUrl = async (invoiceId) => {
    // Check cache first
    if (pdfCache.value[invoiceId]) {
        return pdfCache.value[invoiceId];
    }

    // If not in cache, fetch it
    const response = await InvoiceService.fetchGeneratedInvoicePDFById(invoiceId);
    const url = response.data.url;

    // Cache the URL
    pdfCache.value[invoiceId] = url;
    return url;
};

const handleInvoiceClick = async (invoice) => {
    // Clear existing PDF first
    pdfUrl.value = null;
    shouldShowPdf.value = false;
    
    isLoading.value = true;
    console.log(`Invoice clicked: ${invoice.month} ${invoice.year}`);
    
    try {
        await nextTick(); // Wait for PDF component to be destroyed
        
        const pdfUrlLookup = await getPdfUrl(invoice._id);
        const blob = await fetchSignedPdf(pdfUrlLookup);
        const reader = new FileReader();
        reader.onload = () => {
            pdfUrl.value = reader.result;
            shouldShowPdf.value = true;
        };
        reader.readAsDataURL(new Blob([blob], { type: 'application/pdf' }));

    } catch (error) {
        console.error('Error loading PDF:', error);
    } finally {
        isLoading.value = false;
    }
};


async function fetchSignedPdf(signedUrl) {
    const pdfResponse = await fetch(signedUrl);
        if (!pdfResponse.ok) throw new Error('Failed to fetch PDF');
        return await pdfResponse.blob();
}

async function fetchInvoices() {
    await InvoiceService.fetchInvoices()
        .then((response) => {
            months.value = getDistinctMonths(response.data);
            invoices.value = response.data;
        }).catch(error => {
            console.error('Error fetching invoices:', error);
        });
}

const downloadInvoice = async (invoice) => {
    try {
        const signedUrl = await getPdfUrl(invoice._id);

        // Fetch the PDF content
        const blob = await fetchSignedPdf(signedUrl);
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `${invoice.invoiceNumber}.pdf`;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error downloading invoice:', error);
    }
};

const userBalance = ref(0);
const walletId = ref("");
const userBalanceloading = ref(true);

const fetchWalletData = async () => {
    try {
        const response = await WalletService.getWalletBalance();
        userBalance.value = response.data.balance;
        walletId.value = response.data.walletId;
    } catch (error) {
        console.error('Failed to fetch wallet data:', error);
    } finally {
        userBalanceloading.value = false;
    }
};

const isWithdrawalModalVisible = ref(false);
const isTransactionsVisible = ref(false);
const toggleTransactions = () => {
    isTransactionsVisible.value = !isTransactionsVisible.value;
};

const { canWithdraw, withdrawalMessage } = useWalletWithdrawal(userBalance, user.value);

onMounted(async () => {
    await Promise.all([fetchInvoices(), fetchWalletData()]);
});

</script>

<style scoped>
.invoice-section {
    display: flex;
    /* Change to row to align horizontally */
    /* height: 200px; */
    padding: 0;
    margin: 3px;
    background: #fff;
    border-radius: 10px;
    /* Allow horizontal scrolling if needed */
    white-space: nowrap;
    /* Prevent wrapping */
    /* justify-content: flex-end; */
    /* Aligns items to the start */
    /* Align items vertically centered */
}

.invoice-container {
    display: flex;
    perspective: 1000px;
    perspective-origin: 50% 50%;
    flex-wrap: nowrap;
    height: 199px;

    /* Prevent wrapping of the invoices */
    overflow-x: auto;
    /* Enable horizontal scrolling */
    padding: 10px;
    gap: 10px;
    /* Adds some space between invoices */
}



.invoice {
    position: relative;
    transform-style: preserve-3d;
    transition: transform 0.3s, margin-right 0.3s;
    transform: scale(0.8) translateY(10px) rotateY(25deg);
    width: 50px;
}

.invoice:hover {
    transform: scale(0.9) translateY(15px) translateX(-15px) rotateY(19deg);
    margin-right: 30px;
}

.invoice:hover .invoice-download {
    color: white;
    transition: transform 0.2s, opacity 0.2s;
    transform: translateX(0%);
    transition-delay: 0.2s;
    opacity: 1;
}

.invoice-thumbnail {
    background-image: url('@/assets/test-invoice.png');
    background-size: cover;
    height: 140px;
    background-color: rgba(255, 255, 255, 0.5);
    box-shadow: 0 0 0 1px #000033, -17px 5px 7px -9px rgba(0, 0, 0, 0.2);
    padding: 10px;
    width: 190px;
    border: 10px solid #FFFFFF;
    cursor: pointer;
    box-sizing: inherit;
    color: #000033;
    font-weight: 800;
    position: relative;
    transform: rotateX(7deg);
    transform-origin: 50% 100%;
    border-width: 10px 10px 10px 30px;
}

.invoice-month {
    transform: rotate(270deg) translateY(21px) translateX(-3px);
    font-size: 17px;
    left: -26px;
    bottom: 0;
    position: absolute;
    width: 125px;
    transform-origin: 0 100%;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
}

.invoice-download {
    transition: none;
    color: #000033;
    position: absolute;
    bottom: -35px;
    left: -30px;
    opacity: 0;
    cursor: pointer;
    text-align: center;
    margin-left: 5px;
    font-size: 16px;
    transform: translateX(70%);
    padding-top: 10px;
}

.invoice-data {
    height: 75svh;
    width: 90svw;
}

@media (min-width: 768px) {
    .invoice-data {
        width: 50svw;
    }
}

.text-center {
    text-align: center;
    width: 100%;
}

.pdf-viewer {
    border: 1px solid #ddd;
    border-radius: 4px;
    width: 100%;
    height: 600px;
}

.skeleton-viewer {
    height: 75vh;
    width: 100svw;
}

@media (min-width: 768px) {
    .skeleton-viewer {
        width: 50svw;
    }
}
</style>